import React, { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateStageHintDto,
  GetStageHintDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { HintModalEditor } from '../../components/TimeLine/HintModalEditor';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { HintsSearch } from '../../components/TimeLine/HintsSearch';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../translations';
import {
  convert,
  validate,
} from '../../../common/pokCore/contexts/StageHintContext';

export const HintsView: React.FC = () => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('projectLng');

  const [hint, setHint] = useState<GetStageHintDto>();
  const [showNew, setShowNew] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  const handleAdd = () => {
    setShowNew(true);
    setHint(undefined);
  };

  const addEditHint = async (hintCU: CreateUpdateStageHintDto, id?: string) => {
    const status = await validate(hintCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.stageHints.update(id, hintCU);
        } else {
          await pok.stageHints.create(hintCU);
        }
        setShowNew(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleCloseNew = () => {
    setShowNew(false);
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.stageHints.getAllByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.stageHints, t, tk],
  );

  const getCount = useMemo(
    () =>
      ((filterText: string, filterColumns?: string) =>
        pok.stageHints.getAllCount(
          filterText,
          filterColumns,
        )) as GridCountFunction,
    [pok.stageHints],
  );

  const handleClick = async (id: string) => {
    const getHint = await pok.stageHints.getById(id);
    setHint(getHint);
    setShowNew(true);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.timeLine.timeLineHints)}</Card.Title>
        <Button variant="outline-primary" onClick={handleAdd}>
          {t(tk.timeLine.newHint)}
        </Button>
        <HintsSearch
          getData={getData}
          getCount={getCount}
          onRowClick={handleClick}
          refresh={refresh}
        />
        <HintModalEditor
          id={hint?.id}
          hint={convert(hint)}
          onCompleted={addEditHint}
          show={showNew}
          handleClose={handleCloseNew}
        />
      </Card.Body>
    </Card>
  );
};
